import React, { useState, useEffect } from "react";
import { Box, Dialog, CircularProgress } from "@material-ui/core";
import AppBarComponent from "../../../Common/Headers/AppBarComponent";
import { Transition } from "../../../../utils/ModalHelpers";
import DelayedRedirect from "../../../../utils/DelayedRedirect";
import { useParams } from "react-router-dom";
import { useMessageTemplateData } from "./hooks/useMessageTemplateData";
import { formatOutlets } from "./utils/optionFormatters";
import MessageTemplateForm from "./components/MessageTemplateForm";
import SaveWithoutPreviewConfirmationModal from "./components/SaveWithoutPreviewConfirmationModal";

const MessageTemplatesCreation = ({
	resources,
	defaultObjectType,
	language,
	accessibleMoboOutlets,
	dealerGroupId,
}) => {
	const { templateId } = useParams();
	const [dialogOpen, setDialogOpen] = useState(true);
	const [showPreview, setShowPreview] = useState(false);
	const [dataSaved, setDataSaved] = useState(false);

	const {
		loading,
		saving,
		redirect,
		setRedirect,
		formData,
		brands,
		useCases,
		outlets,
		loadingStates,
		errors,
		saveMessageTemplate,
		handleObjectTypeChange,
		handleMessageTypeChange,
		handleUseCaseChange,
		handleBrandChange,
		handleIsWaitsOnPremiseDependentChange,
		handleIsWaitsOnPremiseChange,
		handleBodyChange,
		handleLanguageChange,
		handleOutletChange,
		markPreviewAsViewed,
		showPreviewWarning,
		setShowPreviewWarning,
		handleContinueSaveWithoutPreview,
	} = useMessageTemplateData(
		resources,
		templateId,
		defaultObjectType,
		language,
		accessibleMoboOutlets
	);

	const handleClose = () => {
		setDialogOpen(false);
		setRedirect({
			pathname: "/messagetemplates",
			state: { refreshGrid: dataSaved },
		});
	};

	const handleSave = async () => {
		const result = await saveMessageTemplate();
		if (result.success) {
			if (!templateId) {
				setDialogOpen(false);
				setRedirect({
					pathname: "/messagetemplates",
					state: { refreshGrid: true },
				});
			} else {
				setDataSaved(true);
			}
		}
	};

	const handleViewPreview = () => {
		setShowPreviewWarning(false);
		setShowPreview(true);
	};

	const handleContinueWithoutPreview = async () => {
		const result = await handleContinueSaveWithoutPreview();
		if (result.success) {
			if (!templateId) {
				setDialogOpen(false);
				setRedirect({
					pathname: "/messagetemplates",
					state: { refreshGrid: true },
				});
			}
		}
	};

	useEffect(() => {
		if (showPreview) {
			const timer = setTimeout(() => {
				setShowPreview(false);
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [showPreview]);

	const isSaveButtonDisabled =
		!formData.body ||
		formData.body.trim() === "" ||
		!formData.language ||
		formData.language.trim() === "" ||
		!formData.useCase ||
		!formData.useCase.id ||
		formData.useCase.id === 0;

	return (
		<Box>
			{redirect && <DelayedRedirect to={redirect} delay={250} />}
			<Dialog
				fullScreen
				open={dialogOpen}
				onClose={handleClose}
				TransitionComponent={Transition}
			>
				<AppBarComponent
					idName="viewMessageTemplate"
					title={
						templateId
							? resources["EditMessageTemplate"]
							: resources["NewMessageTemplate"]
					}
					resources={resources}
					handleClose={handleClose}
					handleSubmit={handleSave}
					disabled={loading || saving || isSaveButtonDisabled}
				/>
				{loading || saving ? (
					<Box className="centered-loader fadeIn">
						<CircularProgress />
					</Box>
				) : (
					<MessageTemplateForm
						resources={resources}
						formData={formData}
						brands={brands}
						useCases={useCases}
						formattedOutlets={formatOutlets(outlets)}
						loadingStates={loadingStates}
						templateId={templateId}
						errors={errors}
						handleObjectTypeChange={handleObjectTypeChange}
						handleMessageTypeChange={handleMessageTypeChange}
						handleUseCaseChange={handleUseCaseChange}
						handleBrandChange={handleBrandChange}
						handleIsWaitsOnPremiseDependentChange={
							handleIsWaitsOnPremiseDependentChange
						}
						handleIsWaitsOnPremiseChange={handleIsWaitsOnPremiseChange}
						handleBodyChange={handleBodyChange}
						handleLanguageChange={handleLanguageChange}
						handleOutletChange={handleOutletChange}
						dealerGroupId={dealerGroupId}
						markPreviewAsViewed={markPreviewAsViewed}
						showPreview={showPreview}
					/>
				)}

				<SaveWithoutPreviewConfirmationModal
					open={showPreviewWarning}
					onClose={handleViewPreview}
					onConfirm={handleContinueWithoutPreview}
					resources={resources}
				/>
			</Dialog>
		</Box>
	);
};

export default MessageTemplatesCreation;
